import React, { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { makePdf } from "./Reports";
import { ID_KINDS, dmy, recName, VALIDITY } from "../lib/identity";
import { useIdTemple, TemplePicker, PageHead, StatusChip, ValidityChip, fetchAndPrint } from "../components/IdentityKit";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FileDown, FileSpreadsheet, RefreshCw, Printer } from "lucide-react";
import { toast } from "sonner";

const todayStr = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const detail = (r) => r.vehicle_number ? `${r.vehicle_number}${r.vehicle_type ? ` (${r.vehicle_type})` : ""}` : r.designation || (r.kind === "children" ? [r.age && `Age ${r.age}`, r.gender, r.father_name && `F: ${r.father_name}`].filter(Boolean).join(", ") : r.city || "");

export default function IdentityReports() {
  const { user } = useAuth();
  const isSA = user.role === "SUPER_ADMIN";
  const { temples, templeId, setTempleId } = useIdTemple(user);
  const [f, setF] = useState({ kind: "", date_mode: "created", frm: todayStr(), to: todayStr(), status: "", department: "", location: "" });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const tparam = isSA ? templeId : "";

  const run = async () => {
    setBusy(true);
    try {
      const kinds = f.kind ? [f.kind] : Object.keys(ID_KINDS);
      const all = await Promise.all(kinds.map((k) => api.get(`/identity/${k}`, { params: { temple_id: tparam, frm: f.frm, to: f.to, date_mode: f.date_mode, status: f.status } })
        .then((r) => r.data.map((x) => ({ ...x, kind: k })))));
      setRows(all.flat().sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))));
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  useEffect(() => { if (templeId || !isSA) run(); }, [templeId]); // eslint-disable-line

  const shown = useMemo(() => rows.filter((r) => (!f.department || r.department_name === f.department) && (!f.location || r.location_name === f.location)), [rows, f.department, f.location]);
  const depts = [...new Set(rows.map((r) => r.department_name).filter(Boolean))].sort();
  const locs = [...new Set(rows.map((r) => r.location_name).filter(Boolean))].sort();
  const summary = useMemo(() => Object.keys(ID_KINDS).map((k) => {
    const s = shown.filter((r) => r.kind === k);
    return { k, total: s.length, approved: s.filter((r) => ["APPROVED", "ISSUED"].includes(r.status)).length, pending: s.filter((r) => r.status === "PENDING").length,
      rejected: s.filter((r) => r.status === "REJECTED").length, valid: s.filter((r) => r.validity === "VALID").length };
  }), [shown]);

  const temple = temples.find((t) => t.temple_id === templeId);
  const period = `${dmy(f.frm)} to ${dmy(f.to)} (${f.date_mode === "valid" ? "valid during" : "registered"})`;
  const head = ["S.No", ...(isSA && !templeId ? ["Temple"] : []), "Type", "Pass / Tag No", "Name", "Details", "Department", "Location / Ghat", "Mobile", "Valid From", "Valid To", "Status", "Validity", "Registered"];
  const line = (r, i) => [i + 1, ...(isSA && !templeId ? [r.temple_id] : []), ID_KINDS[r.kind].label, r.kind === "children" ? r.tag_serial : r.pass_no, recName(r), detail(r),
    r.department_name || "-", r.location_name || "-", r.mobile, dmy(r.valid_from), dmy(r.valid_to), r.status, (VALIDITY[r.validity] || [r.validity])[0], dmy(r.created_at)];

  const exportPdf = () => {
    if (!shown.length) { toast.error("No records to export"); return; }
    makePdf({ temple: isSA && !templeId ? null : temple, templeName: isSA && !templeId ? "All Temples" : undefined,
      title: `Identity & Passes Report - ${period}`, landscape: true, fontSize: 6.5, head, body: shown.map((r, i) => line(r, i).map(String)),
      filename: `Identity_Passes_${f.frm}_${f.to}.pdf` });
  };
  const exportExcel = async () => {
    if (!shown.length) { toast.error("No records to export"); return; }
    const XLSX = await import("xlsx");
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...shown.map(line)]), "Passes");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary.map((s) => ({ Type: ID_KINDS[s.k].plural, Total: s.total, "Approved / Issued": s.approved, Pending: s.pending, Rejected: s.rejected, "Valid today": s.valid }))), "Summary");
    XLSX.writeFile(wb, `Identity_Passes_${f.frm}_${f.to}.xlsx`);
  };

  return (
    <div data-testid="id-reports">
      <PageHead eyebrow="Identity & Passes" title="Reports" subtitle={`${isSA && !templeId ? "All temples" : temple?.name || ""} · ${period}`}>
        <TemplePicker user={user} temples={temples} value={templeId} onChange={setTempleId} allowAll />
      </PageHead>
      <Card className="mb-4"><CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 items-end">
        <div><Label className="text-xs">Type</Label>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm" data-testid="idr-kind">
            <option value="">All types</option>{Object.entries(ID_KINDS).map(([k, v]) => <option key={k} value={k}>{v.plural}</option>)}
          </select></div>
        <div><Label className="text-xs">Date means</Label>
          <select value={f.date_mode} onChange={(e) => setF({ ...f, date_mode: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm" data-testid="idr-mode">
            <option value="created">Registered on</option><option value="valid">Valid during</option>
          </select></div>
        <div><Label className="text-xs">From</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} data-testid="idr-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={f.to} min={f.frm} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="idr-to" /></div>
        <div><Label className="text-xs">Status</Label>
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
            <option value="">All</option>{["PENDING", "APPROVED", "REJECTED", "ISSUED", "RETURNED"].map((s) => <option key={s}>{s}</option>)}
          </select></div>
        <div><Label className="text-xs">Department</Label>
          <select value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
            <option value="">All</option>{depts.map((x) => <option key={x}>{x}</option>)}
          </select></div>
        <div><Label className="text-xs">Location / Ghat</Label>
          <select value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
            <option value="">All</option>{locs.map((x) => <option key={x}>{x}</option>)}
          </select></div>
        <Button onClick={run} disabled={busy} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="idr-run"><RefreshCw className="w-4 h-4 mr-1" /> View</Button>
      </CardContent></Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {summary.map((s) => (
          <Card key={s.k} className="card-gold-top"><CardContent className="p-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{ID_KINDS[s.k].plural}</p>
            <p className="text-2xl font-extrabold text-maroon-900">{s.total}</p>
            <p className="text-[11px] text-slate-600">{s.approved} {s.k === "children" ? "issued" : "approved"} · {s.pending} pending · {s.rejected} rejected · <span className="text-green-700 font-semibold">{s.valid} valid today</span></p>
          </CardContent></Card>
        ))}
      </div>

      <Card><CardContent className="p-3">
        <div className="flex justify-end gap-2 mb-2">
          <Button variant="outline" onClick={exportPdf} data-testid="idr-pdf"><FileDown className="w-4 h-4 mr-1" /> PDF</Button>
          <Button variant="outline" onClick={exportExcel} data-testid="idr-excel"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="idr-table">
            <thead><tr className="bg-maroon-800 text-white text-left text-xs">{[...head, ""].map((h, i) => <th key={i} className="px-2 py-2 whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={head.length + 1} className="py-10 text-center text-slate-400">No records for these filters</td></tr>}
              {shown.map((r, i) => {
                const v = line(r, i);
                const si = head.indexOf("Status"), vi = head.indexOf("Validity");
                return (
                  <tr key={r.kind + r.id} className="border-b border-[#F0E6D2]">
                    {v.map((c, j) => <td key={j} className={`px-2 py-1.5 ${j === head.indexOf("Pass / Tag No") ? "font-mono text-xs whitespace-nowrap" : ""} ${j === head.indexOf("Type") ? "whitespace-nowrap" : ""}`}>{j === si ? <StatusChip s={r.status} /> : j === vi ? <ValidityChip v={r.validity} /> : c}</td>)}
                    <td className="px-2 py-1.5">{["APPROVED", "ISSUED"].includes(r.status) && <Button size="sm" variant="ghost" title="Print" onClick={() => fetchAndPrint(r.kind, [r], temples)}><Printer className="w-4 h-4" /></Button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {shown.length > 0 && <p className="text-xs text-slate-500 mt-2">{shown.length} records</p>}
      </CardContent></Card>
    </div>
  );
}
